import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { fariaLimaArticles, type FariaLimaArticle } from '@/mocks/fariaLimaBlogData';
import { contactInfo } from '@/mocks/homeData';
import { useFadeIn } from '@/hooks/useFadeIn';
import Breadcrumb from '@/components/base/Breadcrumb';

const relatedServiceLinksMap: Record<string, { title: string; slug: string; icon: string; reason: string }[]> = {
  'odontologia-executiva-faria-lima': [
    { title: 'Avaliação Odontológica', slug: '/servicos/avaliacao-odontologica', icon: 'ri-search-eye-line', reason: 'Agende seu check-up executivo com tecnologia 3D em 40 minutos' },
    { title: 'Clareamento Dental', slug: '/servicos/clareamento-dental', icon: 'ri-sun-line', reason: 'Resultado em 1h30 — ideal para o horário de almoço' },
  ],
  'clareamento-dental-agenda-corporativa-faria-lima': [
    { title: 'Clareamento Dental', slug: '/servicos/clareamento-dental', icon: 'ri-sun-line', reason: 'Conheça o protocolo completo de clareamento profissional' },
    { title: 'Avaliação Odontológica', slug: '/servicos/avaliacao-odontologica', icon: 'ri-search-eye-line', reason: 'Primeiro passo: avaliação completa antes do clareamento' },
  ],
  'invisalign-executivos-faria-lima': [
    { title: 'Invisalign', slug: '/servicos/invisalign', icon: 'ri-emotion-happy-line', reason: 'Conheça o tratamento ortodôntico invisível preferido dos executivos' },
    { title: 'Avaliação Odontológica', slug: '/servicos/avaliacao-odontologica', icon: 'ri-search-eye-line', reason: 'Faça seu escaneamento 3D e veja a simulação do seu sorriso' },
  ],
  'saude-bucal-produtividade-faria-lima': [
    { title: 'Avaliação Odontológica', slug: '/servicos/avaliacao-odontologica', icon: 'ri-search-eye-line', reason: 'Check-up preventivo em 40 minutos na Faria Lima' },
    { title: 'Implantes Dentários', slug: '/servicos/implantes-dentarios', icon: 'ri-check-double-line', reason: 'A solução definitiva para dentes perdidos' },
  ],
  'facetas-dentarias-alto-padrao-faria-lima': [
    { title: 'Facetas Dentárias', slug: '/servicos/facetas', icon: 'ri-sparkling-line', reason: 'Transforme seu sorriso com porcelana de alto padrão' },
    { title: 'Clareamento Dental', slug: '/servicos/clareamento-dental', icon: 'ri-sun-line', reason: 'Combine clareamento + facetas para o sorriso perfeito' },
  ],
  'checkup-odontologico-hora-do-almoco-faria-lima': [
    { title: 'Avaliação Odontológica', slug: '/servicos/avaliacao-odontologica', icon: 'ri-search-eye-line', reason: 'Agende seu check-up na hora do almoço — 40 minutos' },
    { title: 'Clareamento Dental', slug: '/servicos/clareamento-dental', icon: 'ri-sun-line', reason: 'Outro procedimento que cabe no almoço: clareamento em 1h30' },
  ],
};

function RelatedServices({ article }: { article: FariaLimaArticle }) {
  const services = relatedServiceLinksMap[article.slug];
  if (!services || services.length === 0) return null;

  return (
    <div className="mt-10 pt-8 border-t border-background-200">
      <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
        Serviços relacionados na <span className="italic font-normal text-foreground-500">Faria Lima</span>
      </h3>
      <p className="text-foreground-500 text-sm mb-5">
        Este artigo menciona procedimentos disponíveis no consultório da Dra. Marihana. Confira as páginas completas de cada serviço para saber mais sobre indicações, valores e agendamento.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {services.map((svc) => (
          <Link
            key={svc.slug}
            to={svc.slug}
            className="group flex items-start gap-4 bg-background-100 hover:bg-background-200/80 rounded-xl p-4 transition-all duration-300 cursor-pointer"
          >
            <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-50 rounded-xl group-hover:bg-accent-100 transition-colors">
              <i className={`${svc.icon} text-accent-600 text-lg`}></i>
            </span>
            <div className="min-w-0">
              <h4 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                {svc.title}
              </h4>
              <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{svc.reason}</p>
            </div>
            <span className="flex-shrink-0 self-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-arrow-right-line text-foreground-400"></i>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ArticleHero({ article }: { article: FariaLimaArticle }) {
  return (
    <section className="relative w-full h-[380px] md:h-[520px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
      </div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pb-12 md:pb-16">
        <span className="inline-block text-xs font-semibold text-accent-300 bg-accent-900/30 backdrop-blur-sm px-3 py-1 rounded-full mb-4">
          {article.category}
        </span>
        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
          {article.title}
        </h1>
        <div className="flex items-center gap-4 text-white/70 text-xs md:text-sm">
          <span>{article.date}</span>
          <span className="w-1 h-1 rounded-full bg-white/40"></span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </section>
  );
}

function ArticleAuthor() {
  return (
    <div className="flex items-center gap-4 py-6 border-y border-background-200">
      <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
        <img
          src="https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/1ca235bb-2c12-4122-92ee-26d36759c454_compressed_664848ED-E73E-4038-A318-6F513F65A05D.JPG.webp"
          alt="Dra. Marihana Pasquali"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div>
        <p className="font-semibold text-sm text-foreground-950">Dra. Marihana Pasquali</p>
        <p className="text-xs text-foreground-500">Cirurgiã Dentista — CRO-SP 172094 | Faria Lima, São Paulo</p>
      </div>
    </div>
  );
}

const articleContentMap: Record<string, { type: string; text?: string; items?: string[] }[]> = {
  'odontologia-executiva-faria-lima': [
    { type: 'paragraph', text: 'Quem trabalha na Faria Lima sabe: o tempo é o recurso mais escasso. Entre reuniões, apresentações e prazos apertados, cuidar da saúde bucal muitas vezes fica em último lugar na lista de prioridades. Mas e se eu te disser que é perfeitamente possível manter um sorriso impecável sem comprometer sua agenda corporativa? É exatamente isso que a odontologia executiva proporciona.' },
    { type: 'heading', text: 'O que é odontologia executiva e por que ela existe?' },
    { type: 'paragraph', text: 'A odontologia executiva é uma abordagem pensada para profissionais com agendas intensas. Consultas com hora marcada e pontualidade absoluta, procedimentos concentrados em menos sessões, atendimento VIP com máxima eficiência e zero perda de tempo. Na Faria Lima, onde cada hora vale ouro, esse modelo não é um luxo — é uma necessidade.' },
    { type: 'paragraph', text: 'No nosso consultório na Avenida Brigadeiro Faria Lima, 2355, desenvolvemos protocolos específicos para atender executivos, banqueiros, advogados e empreendedores. O segredo está no planejamento minucioso e na tecnologia de ponta: scanner intraoral 3D elimina moldagens demoradas, tomografia computadorizada agiliza diagnósticos, e a sedação consciente permite procedimentos mais longos em uma única sessão, com zero desconforto.' },
    { type: 'list', items: [
      'Consultas com horário garantido e pontualidade absoluta — sem filas, sem espera',
      'Procedimentos otimizados: o que antes levava 4 sessões, fazemos em 2',
      'Tecnologia 3D que reduz tempo de cadeira em até 40%',
      'Sedação consciente para múltiplos procedimentos em uma única visita',
      'Comunicação direta por WhatsApp com a própria dentista',
      'Horários estendidos: atendimento até as 19h em dias úteis e sábado pela manhã',
    ]},
    { type: 'heading', text: 'A hora do almoço é sua aliada' },
    { type: 'paragraph', text: 'Muitos pacientes da Faria Lima agendam seus procedimentos durante o horário de almoço. Clareamento dental (1h30), avaliação com scanner 3D (40 minutos), restaurações estéticas (1 hora) — todos perfeitamente encaixáveis em uma pausa prolongada. E o melhor: você volta ao trabalho com o procedimento concluído, sem ninguém perceber que acabou de sair do dentista.' },
    { type: 'heading', text: 'Por que a Faria Lima concentra a odontologia de alto padrão de São Paulo?' },
    { type: 'paragraph', text: 'A região da Faria Lima abriga não apenas as maiores empresas e fundos de investimento do Brasil, mas também a maior concentração de profissionais de alta renda da América Latina. Com isso, naturalmente atrai os melhores especialistas em odontologia, as tecnologias mais avançadas e os tratamentos mais sofisticados. Estar na Faria Lima significa ter acesso ao que há de mais moderno em saúde bucal — e estar a poucos minutos de distância disso.' },
    { type: 'heading', text: 'O custo da negligência bucal no mundo corporativo' },
    { type: 'paragraph', text: 'Pesquisas mostram que profissionais com sorrisos saudáveis e alinhados são percebidos como 58% mais confiáveis em negociações. Além disso, problemas dentários não tratados — cáries, infecções gengivais, dores crônicas — afetam diretamente a concentração, o sono e a disposição. Uma dor de dente persistente pode reduzir a produtividade em até 30%. Investir em saúde bucal é investir em performance profissional.' },
    { type: 'heading', text: 'Como começar seu plano de saúde bucal executiva' },
    { type: 'paragraph', text: 'O primeiro passo é uma avaliação completa com tecnologia 3D. Em 40 minutos, você recebe um diagnóstico completo da sua saúde bucal, uma simulação digital dos resultados possíveis e um plano de tratamento personalizado — com prazos, etapas e investimento transparente. Tudo pensado para se encaixar na sua rotina, não o contrário. Agende pelo WhatsApp e descubra como é simples cuidar do sorriso na Faria Lima.' },
  ],
  'clareamento-dental-agenda-corporativa-faria-lima': [
    { type: 'paragraph', text: 'Você tem uma apresentação importante na segunda-feira. Ou um evento de networking. Ou simplesmente quer um sorriso mais brilhante para as fotos do LinkedIn. A boa notícia é que o clareamento dental profissional — de verdade, com resultado visível e seguro — cabe perfeitamente na sua agenda de trabalho na Faria Lima.' },
    { type: 'heading', text: 'Clareamento na hora do almoço: mito ou realidade?' },
    { type: 'paragraph', text: 'Realidade absoluta. Uma sessão de clareamento profissional em consultório dura em média 1h30. Isso significa que você pode agendar às 12h, realizar o procedimento e estar de volta ao escritório na Faria Lima antes das 14h. O resultado é imediato: dentes de 5 a 8 tons mais claros em uma única sessão. Sem dor, sem sensibilidade excessiva e sem que ninguém perceba que você acabou de passar pelo dentista.' },
    { type: 'list', items: [
      'Sessão de 1h30 — perfeita para o horário de almoço',
      'Resultado visível imediatamente após o procedimento',
      'Gel clareador de alta concentração (35-40% peróxido de hidrogênio)',
      'Ativado por LED de última geração para máxima eficiência',
      'Proteção gengival completa com barreira fotopolimerizável',
      'Protocolo personalizado conforme sua sensibilidade dental',
    ]},
    { type: 'heading', text: 'Clareamento de consultório vs. produtos de farmácia: a realidade' },
    { type: 'paragraph', text: 'Os géis e fitas vendidos em farmácias têm concentração de apenas 3% a 10% de peróxido. O resultado é, na melhor das hipóteses, sutil — e na pior, você gasta dinheiro, tempo e ainda corre risco de queimar a gengiva ou causar sensibilidade prolongada. O clareamento profissional usa concentrações de 35% a 40%, aplicadas com total segurança e supervisão de um especialista. Não há comparação possível.' },
    { type: 'heading', text: 'O que esperar nos dias seguintes ao clareamento' },
    { type: 'paragraph', text: 'Nas primeiras 48 horas após o clareamento, recomendamos a "dieta branca": evite café, vinho tinto, açaí, molho shoyu, beterraba e qualquer alimento com pigmentação forte. Isso porque os poros do esmalte dental estão mais abertos e receptivos a manchas. Depois desse período, a vida volta ao normal — inclusive o cafezinho pós-almoço na Faria Lima.' },
    { type: 'heading', text: 'Manutenção: quanto tempo dura o resultado?' },
    { type: 'paragraph', text: 'Com cuidados adequados, o clareamento profissional dura de 1 a 3 anos. Para pacientes da Faria Lima que consomem muito café (realidade de quase todo mundo no mercado financeiro), recomendamos sessões de manutenção anuais de 40 minutos. Isso mantém o sorriso sempre brilhante, sem precisar repetir o procedimento completo.' },
    { type: 'heading', text: 'Invista em você: o sorriso é seu cartão de visitas' },
    { type: 'paragraph', text: 'Na Faria Lima, sua imagem profissional importa. Um sorriso branco e saudável transmite cuidado, confiança e atenção aos detalhes — exatamente as qualidades que o mercado valoriza. Agende seu clareamento profissional e transforme seu sorriso em uma vantagem competitiva.' },
  ],
  'invisalign-executivos-faria-lima': [
    { type: 'paragraph', text: 'Aparelho ortodôntico tradicional está fora de cogitação para a maioria dos profissionais da Faria Lima. Reuniões com investidores, apresentações para diretoria, almoços com clientes — ninguém quer um sorriso metálico nesses contextos. É exatamente por isso que o Invisalign se tornou o tratamento ortodôntico preferido de quem trabalha no coração financeiro de São Paulo.' },
    { type: 'heading', text: 'O que torna o Invisalign perfeito para a rotina corporativa?' },
    { type: 'paragraph', text: 'Três palavras: invisível, removível e previsível. Os alinhadores Invisalign são confeccionados em plástico médico transparente de alta tecnologia — literalmente invisíveis em fotos, vídeos e conversas. Você pode removê-los para comer, beber (sim, inclusive café e vinho) e para escovar os dentes. E o mais importante: o resultado é previsível, com simulação digital 3D antes mesmo de começar o tratamento.' },
    { type: 'list', items: [
      '100% transparentes: ninguém nota que você está em tratamento ortodôntico',
      'Removíveis: tire para reuniões importantes, almoços e eventos',
      'Sem restrições alimentares: coma o que quiser, sem medo de quebrar bráquetes',
      'Planejamento digital ClinCheck: visualize o resultado final antes de começar',
      'Consultas rápidas a cada 6-8 semanas, sem ajustes dolorosos',
      'Higiene bucal normal: escove e use fio dental sem obstáculos',
    ]},
    { type: 'heading', text: 'Invisalign no ambiente corporativo: relatos reais' },
    { type: 'paragraph', text: 'Pacientes que atuam em bancos de investimento, escritórios de advocacia e consultorias na Faria Lima relatam que colegas e clientes simplesmente não percebem o tratamento. "Fiz 14 meses de Invisalign e ninguém no trabalho notou — só elogiavam meu sorriso cada vez mais alinhado", conta um paciente do setor financeiro. Esse é o tipo de experiência que o aparelho fixo simplesmente não pode oferecer.' },
    { type: 'heading', text: 'Quanto tempo dura o tratamento com Invisalign?' },
    { type: 'paragraph', text: 'Casos leves a moderados: 6 a 12 meses. Casos mais complexos: 12 a 18 meses. A única forma de saber o tempo exato do seu caso é através do escaneamento 3D — que fazemos na primeira consulta, em apenas 15 minutos, sem moldagem tradicional (nada de "massinha" na boca).' },
    { type: 'heading', text: 'Disciplina é tudo: o compromisso que o Invisalign exige' },
    { type: 'paragraph', text: 'Os alinhadores devem ser usados de 20 a 22 horas por dia para garantir o resultado no prazo previsto. Ou seja: você só os remove para comer e escovar os dentes. Para profissionais da Faria Lima acostumados com disciplina e metas, esse compromisso costuma ser natural. E o retorno sobre o investimento — um sorriso perfeitamente alinhado sem ninguém ter notado o processo — vale cada hora de uso.' },
    { type: 'heading', text: 'Seu sorriso alinhado começa com um escaneamento de 15 minutos' },
    { type: 'paragraph', text: 'Agende uma avaliação para o escaneamento 3D na Faria Lima. Em 15 minutos, você vê a simulação do seu sorriso alinhado e descobre o tempo e investimento exatos do seu tratamento. Sem compromisso, sem pressão — apenas informação de qualidade para você decidir com segurança.' },
  ],
  'saude-bucal-produtividade-faria-lima': [
    { type: 'paragraph', text: 'Você já faltou ao trabalho por causa de dor de dente? Ou passou o dia inteiro distraído com um incômodo na boca? Não é só você. Problemas de saúde bucal são uma das principais causas de absenteísmo e queda de produtividade no ambiente corporativo — inclusive na Faria Lima, onde a performance é medida em resultados concretos.' },
    { type: 'heading', text: 'Os números que preocupam o mercado corporativo' },
    { type: 'paragraph', text: 'Segundo a Organização Mundial da Saúde, problemas odontológicos causam a perda de mais de 50 milhões de horas de trabalho por ano apenas nos Estados Unidos. No Brasil, uma pesquisa da Associação Brasileira de Odontologia mostrou que 27% dos profissionais já faltaram ao trabalho por causa de dor de dente — e 43% relataram queda de concentração por problemas bucais não tratados.' },
    { type: 'list', items: [
      '50 milhões de horas de trabalho perdidas por ano (EUA) devido a problemas odontológicos',
      '27% dos profissionais brasileiros já faltaram por dor de dente',
      '43% relatam queda de concentração por problemas bucais',
      '58% mais chances de fechar negócios com um sorriso saudável e alinhado',
      'Profissionais com sorriso bem cuidado recebem em média 12% a mais',
    ]},
    { type: 'heading', text: 'O ciclo vicioso da negligência bucal' },
    { type: 'paragraph', text: 'Funciona assim: você está ocupado, adia o check-up, surge uma cárie pequena (que seria resolvida em 30 minutos), a cárie evolui para canal (horas de tratamento, múltiplas sessões), e de repente você está faltando reuniões, cancelando compromissos e gastando muito mais dinheiro do que gastaria com prevenção. Esse ciclo é mais comum do que você imagina entre profissionais da Faria Lima.' },
    { type: 'heading', text: 'Saúde bucal e saúde geral: a conexão que todo executivo precisa conhecer' },
    { type: 'paragraph', text: 'A boca não é um sistema isolado. Doenças periodontais (gengivite e periodontite) estão diretamente ligadas a doenças cardiovasculares, diabetes descontrolada e até complicações respiratórias. Para profissionais sob alto estresse — realidade de quem trabalha na Faria Lima — a inflamação gengival crônica pode ser um fator de risco cardiovascular silencioso e perigoso.' },
    { type: 'heading', text: 'O check-up preventivo como investimento em produtividade' },
    { type: 'paragraph', text: 'Uma avaliação odontológica completa a cada 6 meses leva 40 minutos e detecta problemas no estágio inicial, quando o tratamento é rápido, simples e barato. Compare isso com as horas perdidas, o desconforto e o custo de tratar um problema avançado — e a equação fica clara. Prevenção é o melhor investimento que um profissional da Faria Lima pode fazer pela própria carreira.' },
    { type: 'heading', text: 'Odontologia do sono: o diferencial que poucos conhecem' },
    { type: 'paragraph', text: 'Você ronca? Acorda cansado mesmo depois de 8 horas de sono? Isso pode ser apneia do sono — e o dentista é um dos profissionais que podem diagnosticar e tratar essa condição. Placas de avanço mandibular, confeccionadas sob medida, podem transformar sua qualidade de sono e, por consequência, sua performance diurna na Faria Lima.' },
  ],
  'facetas-dentarias-alto-padrao-faria-lima': [
    { type: 'paragraph', text: 'As facetas dentárias em porcelana são, sem dúvida, o procedimento estético mais procurado por profissionais de alto padrão na Faria Lima. Executivos, advogados, banqueiros e empreendedores descobriram que investir no sorriso é investir na própria marca pessoal — e os resultados falam por si.' },
    { type: 'heading', text: 'Por que as facetas em porcelana são o padrão ouro na Faria Lima?' },
    { type: 'paragraph', text: 'Diferente das resinas compostas (que mancham e desgastam com o tempo), as facetas em porcelana são confeccionadas em dissilicato de lítio — um material cerâmico de alta resistência com propriedades ópticas idênticas ao esmalte natural. Elas não mancham com café, vinho ou cigarro, duram de 15 a 20 anos e o resultado é absolutamente natural. Ninguém percebe que você fez qualquer procedimento — apenas nota que seu sorriso está mais bonito.' },
    { type: 'list', items: [
      'Porcelana de dissilicato de lítio: o melhor material disponível no mercado',
      'Espessura ultrafina (0,2mm a 0,4mm): preservação máxima do dente natural',
      'Não mancham: café, vinho, chá — nada afeta a cor da porcelana',
      'Durabilidade de 15 a 20 anos com manutenção adequada',
      'Resultado 100% natural: ninguém percebe que são facetas',
      'Planejamento digital DSD: você aprova o sorriso antes de começar',
    ]},
    { type: 'heading', text: 'O processo: transparência e previsibilidade' },
    { type: 'paragraph', text: 'Tudo começa com o Digital Smile Design (DSD) — um planejamento digital completo onde desenhamos seu novo sorriso considerando as proporções do seu rosto, sua personalidade e suas expectativas. Depois, fazemos um mockup (simulação em resina direto na boca) para você "experimentar" o resultado antes de qualquer procedimento definitivo. Só avançamos quando você estiver 100% satisfeito com o design.' },
    { type: 'heading', text: 'Investimento e retorno' },
    { type: 'paragraph', text: 'Sim, facetas em porcelana de alto padrão representam um investimento significativo. Mas quando você considera a durabilidade (15-20 anos), a ausência de manutenções complexas e o impacto na sua confiança e imagem profissional, o custo-benefício é excepcional. Na Faria Lima, onde networking e imagem pessoal são ativos estratégicos, o retorno desse investimento vai muito além da estética.' },
    { type: 'heading', text: 'A escolha do profissional faz toda a diferença' },
    { type: 'paragraph', text: 'A qualidade das facetas depende 50% do material e 50% da habilidade do dentista e do ceramista. Trabalhamos com os melhores laboratórios de cerâmica de São Paulo e cada caso recebe atenção artesanal. Não existe "sorriso de prateleira" — cada conjunto de facetas é único, desenhado especificamente para seu rosto.' },
  ],
  'checkup-odontologico-hora-do-almoco-faria-lima': [
    { type: 'paragraph', text: 'Você sabia que é possível fazer um check-up odontológico completo com tecnologia 3D em apenas 40 minutos? Essa é a nova tendência entre profissionais da Faria Lima que não querem — e não podem — perder tempo. O check-up na hora do almoço já é realidade no nosso consultório e está mudando a relação dos executivos com a saúde bucal.' },
    { type: 'heading', text: 'O que cabe em um check-up de 40 minutos?' },
    { type: 'paragraph', text: 'Muito mais do que você imagina. Com a tecnologia que utilizamos, 40 minutos são suficientes para uma avaliação completa e detalhada da sua saúde bucal. Nada de múltiplas consultas, nada de espera, nada de perder uma tarde inteira.' },
    { type: 'list', items: [
      'Exame clínico completo de todos os dentes, gengivas e mucosas',
      'Scanner intraoral 3D: modelo digital da sua boca em 5 minutos',
      'Tomografia computadorizada Cone Beam (se necessário)',
      'Fotografias intraorais de alta definição',
      'Diagnóstico de cáries, problemas gengivais e desgastes',
      'Plano de tratamento personalizado com prazos e investimento',
    ]},
    { type: 'heading', text: 'Por que a prevenção é o melhor negócio na Faria Lima?' },
    { type: 'paragraph', text: 'Uma cárie pequena detectada no check-up custa uma fração do valor de um canal. Uma gengivite tratada precocemente evita cirurgias periodontais complexas. Um desgaste dental identificado cedo pode ser corrigido sem necessidade de facetas. Na odontologia — assim como nos investimentos — a prevenção é sempre o caminho mais inteligente e econômico.' },
    { type: 'heading', text: 'Check-up digital: a diferença que você vê na tela' },
    { type: 'paragraph', text: 'Uma das coisas que mais surpreende os pacientes da Faria Lima é ver sua própria boca na tela em 3D, em tempo real. O scanner intraoral gera um modelo digital preciso que permite visualizar cada dente, cada restauração e cada detalhe. Você entende exatamente o que está acontecendo e participa ativamente das decisões sobre seu tratamento. Nada de depender apenas da palavra do dentista — você vê com seus próprios olhos.' },
    { type: 'heading', text: 'De 6 em 6 meses: o ritmo ideal' },
    { type: 'paragraph', text: 'Recomendamos check-ups semestrais para a maioria dos pacientes. São apenas 80 minutos por ano investidos na sua saúde bucal. Para profissionais da Faria Lima, onde cada minuto conta, essa é uma das melhores relações custo-benefício que existem em termos de saúde e prevenção. Agende seu check-up na hora do almoço e transforme sua relação com o dentista.' },
  ],
};

const defaultContent = [
  { type: 'paragraph', text: 'Este artigo exclusivo foi produzido pela Dra. Marihana Pasquali para pacientes e profissionais da região da Faria Lima, São Paulo. Conteúdo premium sobre odontologia executiva, estética dental e saúde bucal para quem busca o melhor em cuidados odontológicos no coração financeiro do Brasil.' },
  { type: 'paragraph', text: 'Para agendar uma avaliação personalizada com tecnologia 3D na Faria Lima, entre em contato pelo WhatsApp. Atendemos em horários que respeitam sua agenda corporativa, incluindo horário de almoço.' },
];

function ArticleContent({ article }: { article: FariaLimaArticle }) {
  const { ref, isVisible } = useFadeIn(0.05);
  const content = articleContentMap[article.slug] || defaultContent;

  return (
    <div ref={ref} className={`space-y-8 fade-section ${isVisible ? 'is-visible' : ''}`}>
      {content.map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={i} className="text-foreground-700 text-base md:text-lg leading-relaxed">
                {block.text}
              </p>
            );
          case 'heading':
            return (
              <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-tight mt-10">
                {block.text}
              </h2>
            );
          case 'list':
            return (
              <ul key={i} className="space-y-3">
                {block.items?.map((item, j) => (
                  <li key={j} className="flex items-start gap-3 text-foreground-700 text-base leading-relaxed">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-accent-50 rounded-full mt-0.5">
                      <i className="ri-check-line text-accent-600 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

function ArticleTags({ tags }: { tags: string[] }) {
  return (
    <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
      {tags.map((tag) => (
        <span key={tag} className="text-xs font-medium text-secondary-700 bg-secondary-50 px-3 py-1.5 rounded-full">
          {tag}
        </span>
      ))}
    </div>
  );
}

function RelatedFariaLimaArticles({ currentArticle }: { currentArticle: FariaLimaArticle }) {
  const related = fariaLimaArticles.filter((a) => a.id !== currentArticle.id).slice(0, 3);
  const { ref, isVisible } = useFadeIn(0.1);

  if (!related.length) return null;

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-10 fade-section ${isVisible ? 'is-visible' : ''}`}>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Mais artigos exclusivos da Faria Lima
          </h3>
          <p className="text-foreground-500 text-sm mt-2">Conteúdos premium sobre odontologia para profissionais do centro financeiro de São Paulo</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {related.map((article) => (
            <Link
              key={article.id}
              to={`/blog-faria-lima/${article.slug}`}
              className="group bg-background-50 rounded-xl overflow-hidden card-hover cursor-pointer"
            >
              <div className="relative overflow-hidden h-44 img-zoom">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-5">
                <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">
                  {article.category}
                </span>
                <h4 className="font-heading text-sm font-bold text-foreground-950 mt-3 mb-2 leading-snug group-hover:text-primary-600 transition-colors duration-300">
                  {article.title}
                </h4>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCTA() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
          Pronto para transformar seu sorriso na <span className="italic font-normal text-foreground-500">Faria Lima</span>?
        </h3>
        <p className="text-foreground-600 text-base leading-relaxed mb-8">
          Agende sua avaliação com a Dra. Marihana Pasquali. Atendimento premium para quem busca o melhor em odontologia no coração financeiro de São Paulo.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Vim%20pelo%20artigo%20exclusivo%20Faria%20Lima%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
          >
            <i className="ri-whatsapp-line mr-2"></i>
            Agendar Avaliação
          </a>
          <Link
            to="/blog-faria-lima"
            className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
          >
            <i className="ri-article-line mr-2"></i>
            Ver Mais Artigos
          </Link>
        </div>
      </div>
    </section>
  );
}

function ArticleSchema({ article }: { article: FariaLimaArticle }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: 'Dra. Marihana Pasquali',
      jobTitle: 'Cirurgiã Dentista',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dra. Marihana Pasquali',
      logo: {
        '@type': 'ImageObject',
        url: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/f075322b-f3ac-4364-a5fe-73fa28de4de4_compressed_DSC07751---Copia---Copia.webp',
      },
    },
    keywords: article.tags.join(', '),
    articleBody: (articleContentMap[article.slug] || defaultContent)
      .filter((b) => b.type === 'paragraph' || b.type === 'heading')
      .map((b) => b.text)
      .join(' '),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function FariaLimaPostPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = fariaLimaArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
          <p className="text-foreground-600 mb-6">O conteúdo que você procura não está disponível.</p>
          <Link to="/blog-faria-lima" className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3 rounded-full transition-all duration-300 cursor-pointer inline-block">
            Ver todos os artigos Faria Lima
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ArticleSchema article={article} />

      <main className="min-h-screen bg-background-50">
        <ArticleHero article={article} />

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Blog Faria Lima', href: '/blog-faria-lima' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
          <ArticleAuthor />
          <ArticleContent article={article} />
          <RelatedServices article={article} />
          <ArticleTags tags={article.tags} />
        </article>

        <RelatedFariaLimaArticles currentArticle={article} />
        <ArticleCTA />
      </main>
    </>
  );
}